import React, { forwardRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { getCompetitiveStyle, getFundingTypeStyle, THEME, getStatusStyle, protectedLandPill } from '../../utils/constants';
import { fmtRate, getAllEarnings, getDeadlineDisplay } from '../../utils/helpers';

const C = THEME;

const toMd = (val) => (typeof val === 'string' ? val : Array.isArray(val) ? val.join('\n') : String(val || ''));

/* ── Shared sub-component styles (matching FundingDetail) ─────── */

const PrintSection = ({ icon, title, tierLabel, children }) => (
  <div className="print-section" style={{
    marginTop: 12, border: `1px solid ${C.border}`, borderRadius: 10,
    background: C.white, overflow: 'hidden',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  }}>
    {tierLabel && (
      <div style={{
        fontSize: '0.6rem', fontWeight: 700, letterSpacing: 2,
        textTransform: 'uppercase', color: C.textMid,
        padding: '8px 18px 0',
        background: C.pale,
      }}>
        {tierLabel}
      </div>
    )}
    <div className="section-header" style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '12px 18px',
      background: C.pale,
      borderBottom: `1px solid ${C.border}`,
    }}>
      <span style={{ fontSize: '1.1rem' }}>{icon}</span>
      <span style={{ fontSize: '0.82rem', fontWeight: 600, color: C.textDark }}>{title}</span>
    </div>
    <div style={{ padding: '16px 18px' }}>
      {children}
    </div>
  </div>
);

const KiCard = ({ label, children, style }) => (
  <div className="avoid-break" style={{
    background: C.white, border: `1px solid ${C.border}`,
    borderRadius: 10, padding: '14px 16px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    ...style,
  }}>
    <div style={{
      fontSize: '0.66rem', fontWeight: 600, letterSpacing: '1.4px',
      textTransform: 'uppercase', color: C.textMid, marginBottom: 4,
    }}>{label}</div>
    <div style={{ fontSize: '0.88rem', color: C.textDark, fontWeight: 500, lineHeight: 1.4 }}>
      {children}
    </div>
  </div>
);

const AimTag = ({ children, style }) => (
  <span style={{
    background: C.primaryLight, color: C.primary,
    borderRadius: 20, padding: '3px 10px',
    fontSize: '0.72rem', fontWeight: 500,
    display: 'inline-block', ...style,
  }}>{children}</span>
);

const SubHeading = ({ children, style }) => (
  <p style={{
    fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.8px',
    color: C.primary, textTransform: 'uppercase',
    margin: '16px 0 8px', paddingBottom: 5,
    borderBottom: `1px solid ${C.border}`, ...style,
  }}>{children}</p>
);

const Label = ({ children }) => (
  <div style={{
    fontSize: '0.66rem', fontWeight: 600, letterSpacing: '1.4px',
    textTransform: 'uppercase', color: C.textMid, marginBottom: 4,
  }}>{children}</div>
);

const InfoBox = ({ children }) => (
  <div style={{
    background: C.primaryLight,
    borderLeft: `3px solid ${C.primary}`,
    borderRadius: '0 6px 6px 0',
    padding: '10px 14px', fontSize: '0.82rem',
    color: C.primary, marginBottom: 10, lineHeight: 1.5,
  }}>
    {children}
  </div>
);

const mdComponents = {
  h1: ({ children }) => <p style={{ fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.8px', textTransform: 'uppercase', color: C.primary, margin: '12px 0 6px' }}>{children}</p>,
  h2: ({ children }) => <p style={{ fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.8px', textTransform: 'uppercase', color: C.primary, margin: '12px 0 6px' }}>{children}</p>,
  h3: ({ children }) => <p style={{ fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.8px', textTransform: 'uppercase', color: C.primary, margin: '12px 0 6px' }}>{children}</p>,
  p:  ({ children }) => <p style={{ fontSize: '0.85rem', color: C.textMid, lineHeight: 1.7, margin: '4px 0' }}>{children}</p>,
  ol: ({ children, start }) => <ol start={start} style={{ listStyleType: 'decimal', paddingLeft: 22, margin: 0, fontSize: '0.85rem', color: C.textMid, lineHeight: 1.6 }}>{children}</ol>,
  ul: ({ children }) => <ul style={{ listStyle: 'none', padding: '2px 0 4px 14px', margin: '4px 0 0 6px' }}>{children}</ul>,
  li: ({ children }) => (
    <li className="avoid-break" style={{ fontSize: '0.82rem', color: C.textMid, lineHeight: 1.55, padding: '4px 0 4px 18px', position: 'relative' }}>
      <span style={{ position: 'absolute', left: 2, top: 11, width: 7, height: 7, borderRadius: '50%', border: '1.5px solid #615DEC', background: 'transparent', display: 'inline-block' }} />
      {children}
    </li>
  ),
  a:  ({ children, href }) => <span style={{ color: C.primary }}>{children}</span>,
};

/* ═══════════════════════════════════════════════════════════════
   PRINT LAYOUT
   ═══════════════════════════════════════════════════════════════ */
const FundingPrintLayout = forwardRef(({ funding, sections }, ref) => {
  const has = (key) => sections.has(key);

  const allEarnings = getAllEarnings(funding.payment_rates);
  const statusStyle = getStatusStyle(funding.status);
  const competitiveArr = Array.isArray(funding.competitive) ? funding.competitive : (funding.competitive ? [funding.competitive] : []);
  const deliveryPartners = (funding.contacts || []).filter(c =>
    c.role && (c.role.toLowerCase().includes('delivery') || c.role.toLowerCase().includes('partner'))
  );
  const fundCollections = funding.fund_collection || [];
  const heroCode = fundCollections.length > 0
    ? fundCollections.join(' · ')
    : (funding.fund_group ? (Array.isArray(funding.fund_group) ? funding.fund_group.join(' · ') : funding.fund_group) : null);

  const getLocation = () => {
    if (funding.country) return Array.isArray(funding.country) ? funding.country.join(', ') : funding.country;
    const parts = [];
    if (funding.location_england) parts.push('England');
    if (funding.location_scotland) parts.push('Scotland');
    if (funding.location_wales) parts.push('Wales');
    if (funding.location_northern_ireland) parts.push('Northern Ireland');
    if (parts.length) return parts.join(', ');
    if (funding.location?.length) return funding.location.join(', ');
    return 'Not specified';
  };

  const expectedCosts = (funding.costs || []).filter(c => c.cost_type !== 'ineligible');
  const ineligibleCosts = (funding.costs || []).filter(c => c.cost_type === 'ineligible');

  // Work out which section gets each tier label (first visible section in the group)
  const hasWhatToDo = has('whatToDo') && (funding.must_do || funding.must_not_do);
  const hasEvidence = has('evidence') && funding.evidence_requirements;
  const hasMoney = has('money') && (allEarnings.length > 0 || funding.payment_model || funding.costs?.length > 0);
  const hasCompatible = has('compatible') && ((funding.compatible_grants_display?.length > 0) || (Object.keys(funding.compatible_schemes || {}).length > 0));
  const hasWhoCanApply = has('whoCanApply') && (funding.who_can_apply || funding.eligibility_requirements || funding.ineligibility_requirements);
  const hasHowToApply = has('howToApply') && funding.how_to_apply;
  const hasCriteria = has('criteria') && funding.application_criteria;
  const hasConsents = has('consents') && (funding.protected_land?.length > 0 || funding.consent_text);
  const hasContacts = has('contacts') && (funding.contacts?.length > 0 || funding.application_url);

  const detailSections = [hasWhatToDo, hasEvidence, hasMoney, hasCompatible];
  const detailKeys = ['whatToDo', 'evidence', 'money', 'compatible'];
  const firstDetailKey = detailKeys[detailSections.indexOf(true)];

  const appSections = [hasWhoCanApply, hasHowToApply, hasCriteria, hasConsents, hasContacts];
  const appKeys = ['whoCanApply', 'howToApply', 'criteria', 'consents', 'contacts'];
  const firstAppKey = appKeys[appSections.indexOf(true)];

  const pageUrl = `${window.location.origin}/funding/${funding.slug || funding.id}`;

  return (
    <div ref={ref} style={{
      fontFamily: "'Poppins', system-ui, sans-serif",
      color: C.textDark, fontSize: '0.82rem', lineHeight: 1.5,
      background: '#faf9f5',
    }}>

      {/* ── Export banner ─────────────────────────────────────── */}
      <div className="avoid-break" style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 28px',
        background: C.white,
        borderBottom: `1px solid ${C.border}`,
      }}>
        <img src={`${window.location.origin}/oko-logo.png`} alt="Oko" style={{ height: 28 }} />
        <span style={{ fontSize: '0.7rem', color: C.primary }}>
          {pageUrl}
        </span>
      </div>

      {/* ── HERO ──────────────────────────────────────────────── */}
      <div style={{
        background: `linear-gradient(135deg, ${C.primaryDeep} 0%, ${C.primaryMid} 60%, ${C.primary} 100%)`,
        padding: '36px 28px 28px', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ maxWidth: 780, margin: '0 auto' }}>
          {heroCode && (
            <div style={{
              display: 'inline-block', background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6,
              padding: '3px 10px', fontSize: '0.72rem', fontWeight: 600,
              color: 'rgba(255,255,255,0.75)', letterSpacing: '0.5px', marginBottom: 8,
            }}>
              {heroCode}
            </div>
          )}
          <h1 style={{ fontSize: '2rem', fontWeight: 700, color: '#fff', lineHeight: 1.2, margin: '0 0 8px' }}>
            {funding.name || funding.description}
          </h1>

          {funding.funders?.length > 0 && (
            <p style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.65)', margin: '0 0 18px', fontWeight: 300 }}>
              {funding.funders.join(', ')}
            </p>
          )}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {statusStyle && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '4px 12px', borderRadius: 20,
                fontSize: '0.75rem', fontWeight: 600,
                background: statusStyle.bg, color: statusStyle.color,
              }}>
                {statusStyle.label}
              </span>
            )}
            {competitiveArr.map((c, i) => {
              const cs = getCompetitiveStyle(c);
              return (
                <span key={i} style={{
                  display: 'inline-flex', alignItems: 'center',
                  padding: '4px 12px', borderRadius: 20,
                  fontSize: '0.75rem', fontWeight: 600,
                  background: cs.bg, color: cs.color,
                }}>
                  {cs.label}
                </span>
              );
            })}
            {funding.funding_type && (() => {
              const fts = getFundingTypeStyle(funding.funding_type);
              return (
                <span style={{
                  padding: '4px 12px', borderRadius: 20,
                  fontSize: '0.75rem', fontWeight: 600,
                  background: fts.bg, color: fts.color,
                }}>
                  {fts.label}
                </span>
              );
            })()}
          </div>
        </div>
      </div>

      {/* ── Earning strip ─────────────────────────────────────── */}
      {(allEarnings.length > 0 || funding.duration || funding.deadline_date || funding.deadline_tag) && (
        <div className="avoid-break" style={{
          maxWidth: 780, margin: '0 auto',
          background: C.white, border: `1px solid ${C.border}`, borderRadius: 10,
          padding: '16px 20px', marginTop: 14,
          display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center',
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        }}>
          {allEarnings.slice(0, 4).map((e, i) => (
            <div key={i}>
              <Label>Earning</Label>
              <span style={{ fontSize: '1.1rem', fontWeight: 700, color: C.textDark }}>{e.value}</span>
              {e.unit && <span style={{ fontSize: '0.72rem', color: C.textMid, marginLeft: 4 }}>{e.unit}</span>}
            </div>
          ))}
          {(funding.deadline_tag || funding.deadline_date) && (
            <div>
              <Label>Deadline</Label>
              <span style={{ fontSize: '0.92rem', fontWeight: 600, color: C.textDark }}>
                {getDeadlineDisplay(funding)}
              </span>
            </div>
          )}
          {funding.duration && (
            <div>
              <Label>Duration</Label>
              <span style={{ fontSize: '0.92rem', fontWeight: 600, color: C.textDark }}>{funding.duration}</span>
            </div>
          )}
        </div>
      )}

      {/* ── BODY ───────────────────────────────────────────────── */}
      <div style={{ maxWidth: 780, margin: '0 auto', padding: '0 0 28px' }}>

        {/* ── Overview (always included) ─────────────────────── */}
        {has('overview') && (
          <>
            {(funding.executive_summary || funding.description) && (
              <div style={{
                background: C.white, border: `1px solid ${C.border}`,
                borderRadius: 10, padding: 24, marginTop: 20,
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
              }}>
                <p style={{ fontSize: '0.9rem', lineHeight: 1.7, color: C.textMid }}>
                  {funding.executive_summary || funding.description}
                </p>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginTop: 10 }}>
              {funding.funders?.length > 0 && <KiCard label="Funder">{funding.funders.join('; ')}</KiCard>}
              {deliveryPartners.length > 0 && <KiCard label="Delivery Partner">{deliveryPartners.map(d => d.name).join('; ')}</KiCard>}
              <KiCard label="Country">{getLocation()}</KiCard>
            </div>

            {funding.rotational_or_static?.filter(v => v && v.toLowerCase() !== 'not relevant').length > 0 && (
              <KiCard label="Rotational / Static" style={{ marginTop: 10 }}>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {funding.rotational_or_static.filter(v => v && v.toLowerCase() !== 'not relevant').map((v, i) => <AimTag key={i}>{v}</AimTag>)}
                </div>
              </KiCard>
            )}

            {[
              { label: 'Farm Types', data: funding.farm_types },
              { label: 'Eligible Land Types', data: funding.land_types },
              { label: 'Aims', data: funding.aims },
            ].map(({ label, data }) => (
              data?.filter(v => v && v.toLowerCase() !== 'not specified').length > 0 && (
                <KiCard key={label} label={label} style={{ marginTop: 10 }}>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {data.filter(v => v && v.toLowerCase() !== 'not specified').map((v, i) => <AimTag key={i}>{v}</AimTag>)}
                  </div>
                </KiCard>
              )
            ))}
          </>
        )}

        {/* ── Detail sections ─────────────────────────────────── */}

        {/* What To Do */}
        {hasWhatToDo && (
          <>
            <PrintSection icon="📋" title="What To Do & Not Do" tierLabel={firstDetailKey === 'whatToDo' ? 'Detail' : undefined}>
              {funding.must_do && (
                <>
                  <SubHeading style={{ fontSize: '0.66rem', letterSpacing: '1.4px', color: C.textMid, borderBottom: 'none', paddingBottom: 0 }}>Must Do</SubHeading>
                  <ReactMarkdown components={mdComponents}>{toMd(funding.must_do)}</ReactMarkdown>
                </>
              )}
              {funding.must_not_do && (
                <>
                  {funding.must_do && <div style={{ borderTop: '1px solid rgba(0,0,0,0.07)', margin: '14px 0 0' }} />}
                  <SubHeading style={{ fontSize: '0.66rem', letterSpacing: '1.4px', color: C.textMid, borderBottom: 'none', paddingBottom: 0 }}>Must Not Do</SubHeading>
                  <ReactMarkdown components={mdComponents}>{toMd(funding.must_not_do)}</ReactMarkdown>
                </>
              )}
            </PrintSection>
          </>
        )}

        {/* Evidence */}
        {hasEvidence && (
          <>
            <PrintSection icon="📁" title="Evidence Requirements" tierLabel={firstDetailKey === 'evidence' ? 'Detail' : undefined}>
              <ReactMarkdown components={mdComponents}>{toMd(funding.evidence_requirements)}</ReactMarkdown>
            </PrintSection>
          </>
        )}

        {/* Money */}
        {hasMoney && (
          <>
            <PrintSection icon="💷" title="Money" tierLabel={firstDetailKey === 'money' ? 'Detail' : undefined}>
              {funding.payment_rates?.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 14 }}>
                  {funding.payment_rates.map((rate, i) => (
                    <div key={i} className="avoid-break" style={{ background: C.pale, border: `1px solid ${C.border}`, borderRadius: 6, padding: '10px 12px' }}>
                      <Label>{rate.short_description || 'Payment Rate'}</Label>
                      <div style={{ fontSize: '1rem', fontWeight: 700, color: C.textDark }}>
                        {fmtRate(rate.rate_minimum, rate.unit)}
                        {rate.rate_maximum && rate.rate_maximum !== rate.rate_minimum && ` – ${fmtRate(rate.rate_maximum, rate.unit)}`}
                      </div>
                      {rate.unit && <div style={{ fontSize: '0.72rem', color: C.textMid }}>{rate.unit}</div>}
                    </div>
                  ))}
                </div>
              )}

              {funding.payment_model && (
                <div style={{ marginBottom: 10 }}>
                  <Label>Payment Method</Label>
                  <p style={{ fontSize: '0.85rem', color: C.textMid }}>{funding.payment_model}</p>
                  {funding.payment_explainer && (
                    <p style={{ fontSize: '0.82rem', color: C.textMid, fontStyle: 'italic' }}>{funding.payment_explainer}</p>
                  )}
                </div>
              )}

              {funding.funding_value_limit && (
                <div style={{ marginBottom: 10 }}>
                  <Label>Funding Cap</Label>
                  <p style={{ fontSize: '0.88rem', color: C.textDark, fontWeight: 600 }}>
                    {funding.funding_value_limit_unit}{funding.funding_value_limit_value}
                  </p>
                  {funding.funding_value_limit_explainer && (
                    <p style={{ fontSize: '0.82rem', color: C.textMid, fontStyle: 'italic' }}>{funding.funding_value_limit_explainer}</p>
                  )}
                </div>
              )}

              {expectedCosts.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  <SubHeading>Expected Costs</SubHeading>
                  {expectedCosts.map((c, i) => (
                    <div key={i} style={{ fontSize: '0.85rem', color: C.textMid, padding: '3px 0' }}>
                      <strong>{c.header}</strong>{c.detail && ` — ${c.detail}`}
                    </div>
                  ))}
                </div>
              )}

              {ineligibleCosts.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  <SubHeading>Ineligible Costs</SubHeading>
                  {ineligibleCosts.map((c, i) => (
                    <div key={i} style={{ fontSize: '0.85rem', color: C.textMid, padding: '3px 0' }}>
                      <strong>{c.header}</strong>{c.detail && ` — ${c.detail}`}
                    </div>
                  ))}
                </div>
              )}
            </PrintSection>
          </>
        )}

        {/* Compatible Schemes */}
        {hasCompatible && (
          <>
            <PrintSection icon="🔗" title="Compatible Opportunities" tierLabel={firstDetailKey === 'compatible' ? 'Detail' : undefined}>
              {funding.compatible_grants_display?.map((coll, i) => (
                <div key={i} className="avoid-break" style={{ marginBottom: 12 }}>
                  <Label>{coll.collection_name}</Label>
                  {coll.all_compatible_rule && (
                    <p style={{ fontSize: '0.78rem', color: C.textMid, fontStyle: 'italic', margin: '2px 0 6px' }}>{coll.all_compatible_rule}</p>
                  )}
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {coll.codes.map((code, j) => (
                      <AimTag key={j}>{typeof code === 'object' ? code.code : code}</AimTag>
                    ))}
                  </div>
                </div>
              ))}

              {Object.entries(funding.compatible_schemes || {}).map(([pot, schemes]) => (
                <div key={pot} className="avoid-break" style={{ marginBottom: 12 }}>
                  <Label>{pot} ({schemes.length})</Label>
                  <div style={{ fontSize: '0.82rem', color: C.textMid }}>
                    {schemes.map(s => s.name).join(', ')}
                  </div>
                </div>
              ))}
            </PrintSection>
          </>
        )}

        {/* ── Application sections ────────────────────────────── */}

        {/* Who Can Apply */}
        {hasWhoCanApply && (
          <>
            <PrintSection icon="✅" title="Who Can Apply" tierLabel={firstAppKey === 'whoCanApply' ? 'Application' : undefined}>
              {funding.who_can_apply && <InfoBox>{funding.who_can_apply}</InfoBox>}

              {[
                { label: 'Farm Types', data: funding.farm_types },
                { label: 'Eligible Land Types', data: funding.land_types },
                { label: 'RPA Land Cover', data: funding.land_covers },
              ].map(({ label, data }) => (
                data?.filter(v => v && v.toLowerCase() !== 'not specified').length > 0 && (
                  <div key={label} style={{ marginBottom: 12 }}>
                    <Label>{label}</Label>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {data.filter(v => v && v.toLowerCase() !== 'not specified').map((v, i) => (
                        <span key={i} style={{ background: C.primaryLight, color: C.primary, borderRadius: 20, padding: '4px 12px', fontSize: '0.75rem', fontWeight: 500, border: `1px solid rgba(97,93,236,0.2)` }}>{v}</span>
                      ))}
                    </div>
                  </div>
                )
              ))}

              {funding.eligibility_requirements && (
                <>
                  <SubHeading style={{ fontSize: '0.66rem', letterSpacing: '1.4px', color: C.textMid, borderBottom: 'none', paddingBottom: 0 }}>Eligibility Requirements</SubHeading>
                  <ReactMarkdown components={mdComponents}>{toMd(funding.eligibility_requirements)}</ReactMarkdown>
                </>
              )}

              {funding.ineligibility_requirements && (
                <>
                  <div style={{ borderTop: '1px solid rgba(0,0,0,0.07)', margin: '14px 0 0' }} />
                  <SubHeading style={{ fontSize: '0.66rem', letterSpacing: '1.4px', color: C.textMid, borderBottom: 'none', paddingBottom: 0 }}>Ineligibility</SubHeading>
                  <ReactMarkdown components={mdComponents}>{toMd(funding.ineligibility_requirements)}</ReactMarkdown>
                </>
              )}
            </PrintSection>
          </>
        )}

        {/* How To Apply */}
        {hasHowToApply && (
          <>
            <PrintSection icon="🚀" title="How To Apply" tierLabel={firstAppKey === 'howToApply' ? 'Application' : undefined}>
              <ReactMarkdown components={mdComponents}>{toMd(funding.how_to_apply)}</ReactMarkdown>
            </PrintSection>
          </>
        )}

        {/* Application Criteria */}
        {hasCriteria && (
          <>
            <PrintSection icon="📊" title="Application Criteria" tierLabel={firstAppKey === 'criteria' ? 'Application' : undefined}>
              <ReactMarkdown components={mdComponents}>{toMd(funding.application_criteria)}</ReactMarkdown>
            </PrintSection>
          </>
        )}

        {/* Consents & Permissions */}
        {hasConsents && (
          <>
            <PrintSection icon="📜" title="Consents & Permissions" tierLabel={firstAppKey === 'consents' ? 'Application' : undefined}>
              {funding.consent_text && (
                <p style={{ fontSize: '0.85rem', color: C.textMid, lineHeight: 1.6, marginBottom: 16, paddingBottom: 16, borderBottom: `1px solid ${C.border}` }}>
                  {funding.consent_text}
                </p>
              )}

              {funding.protected_land?.length > 0 && (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
                  <thead>
                    <tr>
                      {['Site / Land Type', 'Status'].map(h => (
                        <th key={h} style={{
                          background: C.pale, padding: '7px 12px', textAlign: 'left',
                          fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.8px',
                          textTransform: 'uppercase', color: C.textMid,
                          borderBottom: `2px solid ${C.border}`,
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {funding.protected_land.map((land, i) => {
                      const pill = protectedLandPill(land.status);
                      return (
                        <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                          <td style={{ padding: '6px 12px', color: C.textDark, fontSize: '0.82rem' }}>{land.name}</td>
                          <td style={{ padding: '6px 12px' }}>
                            <span style={{
                              display: 'inline-block', padding: '3px 12px', borderRadius: 20,
                              fontSize: '0.78rem', fontWeight: 600,
                              background: pill.bg, color: pill.color,
                              border: `1px solid ${pill.border}`,
                            }}>
                              {pill.icon} {pill.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </PrintSection>
          </>
        )}

        {/* Contacts */}
        {hasContacts && (
          <>
            <PrintSection icon="📞" title="Contact Information" tierLabel={firstAppKey === 'contacts' ? 'Application' : undefined}>
              {funding.contacts?.map((contact, i) => (
                <div key={i} className="avoid-break" style={{
                  background: C.pale, border: `1px solid ${C.border}`,
                  borderRadius: 8, padding: '12px 14px', marginBottom: 8,
                }}>
                  <div style={{ fontWeight: 600, color: C.textDark, fontSize: '0.85rem' }}>{contact.name}</div>
                  {contact.contact && <div style={{ color: C.textMid, fontSize: '0.82rem' }}>{contact.contact}</div>}
                  {contact.email && <div style={{ color: C.primary, fontSize: '0.82rem' }}>{contact.email}</div>}
                  {contact.phone && <div style={{ color: C.textMid, fontSize: '0.82rem' }}>{contact.phone}</div>}
                  {contact.availability && <div style={{ color: C.textMid, fontSize: '0.78rem', fontStyle: 'italic' }}>{contact.availability}</div>}
                </div>
              ))}

              {funding.application_url && (
                <div style={{ marginTop: 8 }}>
                  <Label>Application URL</Label>
                  <span style={{ fontSize: '0.82rem', color: C.primary, wordBreak: 'break-all' }}>{funding.application_url}</span>
                </div>
              )}

            </PrintSection>
          </>
        )}
      </div>
    </div>
  );
});

FundingPrintLayout.displayName = 'FundingPrintLayout';

export default FundingPrintLayout;
